import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, User, FileText, Truck, CheckCircle, XCircle, Clock, Shield, Eye, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const TABS = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'equipment', label: 'Equipment', icon: Truck },
  { id: 'readiness', label: 'Readiness', icon: Shield },
];

export default function AdminDriverOnboarding() {
  const { driverId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [driver, setDriver] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [equipmentDocs, setEquipmentDocs] = useState({});
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);

  // Reject dialog state
  const [rejectDialog, setRejectDialog] = useState({ open: false, type: null, id: null });
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchData = () => {
    setLoading(true);
    Promise.all([
      api.get('/drivers').then(r => (r.data || []).find(d => d.user_id === driverId)),
      api.get('/documents').then(r => (r.data || []).filter(d => d.user_id === driverId)),
      api.get('/equipment').then(r => (r.data || []).filter(e => e.owner_id === driverId)),
    ]).then(([drv, docs, equip]) => {
      setDriver(drv || null);
      setDocuments(docs || []);
      setEquipment(equip || []);

      // Fetch equipment documents for each piece of equipment
      if (equip && equip.length > 0) {
        Promise.all(
          equip.map(e =>
            api.get(`/equipment/${e.equipment_id}/documents`).then(r => ({ id: e.equipment_id, docs: r.data || [] })).catch(() => ({ id: e.equipment_id, docs: [] }))
          )
        ).then(results => {
          const docsMap = {};
          results.forEach(r => { docsMap[r.id] = r.docs; });
          setEquipmentDocs(docsMap);
        });
      }

      // Compute readiness
      if (drv) {
        const profileComplete = (drv.profile_completion_pct || 0) >= 80;
        const termsAccepted = !!drv.terms_accepted;
        const licenseApproved = docs.some(d => d.document_type === 'drivers_license' && d.status === 'approved');
        const hasEquipment = equip.length > 0;
        const equipmentApproved = hasEquipment && equip.every(e => e.status === 'active');
        const noExpiredDocs = !docs.some(d => d.expiry_date && new Date(d.expiry_date) < new Date() && d.status !== 'rejected');
        setReadiness({ profileComplete, termsAccepted, licenseApproved, equipmentApproved, noExpiredDocs });
      }
    }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [driverId]);

  // Document actions
  const handleApproveDoc = async (docId) => {
    setActionLoading(true);
    try {
      await api.put(`/documents/${docId}/review`, { status: 'approved' });
      toast.success('Document approved');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to approve document'); }
    finally { setActionLoading(false); }
  };

  const handleRejectDoc = async () => {
    if (!rejectReason.trim()) { toast.error('Rejection reason is required'); return; }
    setActionLoading(true);
    try {
      await api.put(`/documents/${rejectDialog.id}/review`, { status: 'rejected', rejection_reason: rejectReason.trim() });
      toast.success('Document rejected');
      setRejectDialog({ open: false, type: null, id: null });
      setRejectReason('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reject document'); }
    finally { setActionLoading(false); }
  };

  // Equipment actions
  const handleApproveEquipment = async (equipId) => {
    setActionLoading(true);
    try {
      await api.put(`/equipment/${equipId}/approve`);
      toast.success('Equipment approved');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to approve equipment'); }
    finally { setActionLoading(false); }
  };

  const handleRejectEquipment = async () => {
    if (!rejectReason.trim()) { toast.error('Rejection reason is required'); return; }
    setActionLoading(true);
    try {
      await api.put(`/equipment/${rejectDialog.id}/reject`, { reason: rejectReason.trim() });
      toast.success('Equipment rejected');
      setRejectDialog({ open: false, type: null, id: null });
      setRejectReason('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reject equipment'); }
    finally { setActionLoading(false); }
  };

  // Driver approval/rejection
  const handleApproveDriver = async () => {
    setActionLoading(true);
    try {
      await api.put(`/users/${driverId}`, { onboarding_completed: true, profile_completion_pct: 100 });
      toast.success('Driver approved and onboarded!');
      navigate('/admin/drivers');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to approve driver'); }
    finally { setActionLoading(false); }
  };

  const handleRejectDriver = async () => {
    if (!rejectReason.trim()) { toast.error('Rejection reason is required'); return; }
    setActionLoading(true);
    try {
      await api.put('/onboarding/profile', { onboarding_completed: false, rejection_reason: rejectReason.trim(), user_id: driverId });
      toast.success('Driver rejected');
      setRejectDialog({ open: false, type: null, id: null });
      setRejectReason('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reject driver'); }
    finally { setActionLoading(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 text-muted-foreground animate-spin" /></div>;
  if (!driver) return <div className="text-center py-12"><p className="text-muted-foreground">Driver not found</p><Button variant="outline" onClick={() => navigate('/admin/drivers')} className="mt-4 border-input text-muted-foreground">Back to Drivers</Button></div>;

  const statusBadge = driver.onboarding_completed
    ? <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">Onboarded</Badge>
    : <Badge className="bg-amber-500/20 text-amber-400 text-xs">Pending Review</Badge>;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate('/admin/drivers')} className="text-muted-foreground hover:text-foreground p-2 h-auto">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-4 flex-1">
          <div className="w-14 h-14 rounded-full bg-brand-accent/20 flex items-center justify-center text-2xl font-bold text-brand-accent">{driver.name?.charAt(0)}</div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{driver.name}</h1>
            <p className="text-sm text-muted-foreground">{driver.email}{driver.phone ? ` · ${driver.phone}` : ''}</p>
          </div>
          <div className="ml-4">{statusBadge}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-6">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-2 text-[10px] tracking-wider font-bold px-4 py-2 rounded-lg transition-colors ${
              activeTab === t.id ? 'bg-brand-accent/10 text-brand-accent border border-brand-accent/20' : 'text-muted-foreground hover:text-foreground border border-transparent'
            }`}>
            <t.icon className="w-3.5 h-3.5" />
            {t.label.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'profile' && <ProfileTab driver={driver} />}
      {activeTab === 'documents' && (
        <DocumentsTab
          documents={documents}
          onApprove={handleApproveDoc}
          onReject={(id) => { setRejectDialog({ open: true, type: 'document', id }); setRejectReason(''); }}
          actionLoading={actionLoading}
        />
      )}
      {activeTab === 'equipment' && (
        <EquipmentTab
          equipment={equipment}
          equipmentDocs={equipmentDocs}
          onApprove={handleApproveEquipment}
          onReject={(id) => { setRejectDialog({ open: true, type: 'equipment', id }); setRejectReason(''); }}
          actionLoading={actionLoading}
        />
      )}
      {activeTab === 'readiness' && (
        <ReadinessTab
          driver={driver}
          readiness={readiness}
          onApproveDriver={handleApproveDriver}
          onRejectDriver={() => { setRejectDialog({ open: true, type: 'driver', id: driverId }); setRejectReason(''); }}
          actionLoading={actionLoading}
        />
      )}

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => { if (!open) { setRejectDialog({ open: false, type: null, id: null }); setRejectReason(''); } }}>
        <DialogContent className="bg-surface border-border text-foreground max-w-md">
          <DialogHeader>
            <DialogTitle className="tracking-wider">
              Reject {rejectDialog.type === 'document' ? 'Document' : rejectDialog.type === 'equipment' ? 'Equipment' : 'Driver'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">Please provide a reason for the rejection. This will be visible to the driver.</p>
          <Textarea
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            placeholder="Enter rejection reason..."
            className="bg-muted border-input text-foreground rounded-lg mt-2 min-h-[100px]"
          />
          <div className="flex gap-2 mt-4 justify-end">
            <Button variant="outline" onClick={() => { setRejectDialog({ open: false, type: null, id: null }); setRejectReason(''); }} className="border-input text-muted-foreground">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (rejectDialog.type === 'document') handleRejectDoc();
                else if (rejectDialog.type === 'equipment') handleRejectEquipment();
                else handleRejectDriver();
              }}
              disabled={actionLoading || !rejectReason.trim()}
              className="bg-red-600 hover:bg-red-700 text-white font-bold">
              {actionLoading ? 'Rejecting...' : 'Reject'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProfileTab({ driver }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Personal Information */}
      <Card className="bg-card border-border">
        <CardContent className="p-5 space-y-4">
          <p className="text-[10px] text-muted-foreground tracking-wider font-bold">PERSONAL INFORMATION</p>
          <Row label="Full Name" value={driver.name || '—'} />
          <Row label="Email" value={driver.email || '—'} />
          <Row label="Phone" value={driver.phone || '—'} />
          <Row label="Date of Birth" value={driver.date_of_birth ? new Date(driver.date_of_birth).toLocaleDateString() : '—'} />
          <Row label="License Number" value={driver.license_number || '—'} />
          <Row label="Truck Type" value={driver.truck_type || '—'} />
        </CardContent>
      </Card>

      {/* Address */}
      <Card className="bg-card border-border">
        <CardContent className="p-5 space-y-4">
          <p className="text-[10px] text-muted-foreground tracking-wider font-bold">ADDRESS</p>
          <Row label="Street" value={driver.street_address || '—'} />
          <Row label="City" value={driver.city || '—'} />
          <Row label="Province" value={driver.province || '—'} />
          <Row label="Postal Code" value={driver.postal_code || '—'} />
        </CardContent>
      </Card>

      {/* Emergency Contact */}
      <Card className="bg-card border-border">
        <CardContent className="p-5 space-y-4">
          <p className="text-[10px] text-muted-foreground tracking-wider font-bold">EMERGENCY CONTACT</p>
          <Row label="Contact Name" value={driver.emergency_contact_name || '—'} />
          <Row label="Phone" value={driver.emergency_contact_phone || '—'} />
          <Row label="Relation" value={driver.emergency_contact_relation || '—'} />
        </CardContent>
      </Card>

      {/* Onboarding Status */}
      <Card className="bg-card border-border">
        <CardContent className="p-5 space-y-4">
          <p className="text-[10px] text-muted-foreground tracking-wider font-bold">ONBOARDING STATUS</p>
          <Row label="Profile Completion" value={`${driver.profile_completion_pct || 0}%`} />
          <Row label="Terms Accepted" value={driver.terms_accepted ? 'Yes' : 'No'} />
          <Row label="Member Since" value={driver.created_at ? new Date(driver.created_at).toLocaleDateString() : '—'} />
          <Row label="Onboarding Completed" value={driver.onboarding_completed ? 'Yes' : 'No'} />
        </CardContent>
      </Card>
    </div>
  );
}

function DocumentsTab({ documents, onApprove, onReject, actionLoading }) {
  if (documents.length === 0) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-8 text-center">
          <FileText className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">No documents uploaded yet</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {documents.map(doc => {
        const statusClasses = doc.status === 'approved'
          ? 'bg-emerald-500/20 text-emerald-400'
          : doc.status === 'rejected'
            ? 'bg-red-500/20 text-red-400'
            : 'bg-amber-500/20 text-amber-400';

        return (
          <Card key={doc.document_id || doc.id} className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <p className="text-sm text-foreground font-medium capitalize">
                      {(doc.document_type || '').replace(/_/g, ' ')}
                    </p>
                    <Badge className={`text-[10px] ${statusClasses}`}>
                      {(doc.status || 'pending').toUpperCase()}
                    </Badge>
                  </div>
                  {doc.file_name && (
                    <a
                      href={doc.file_url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-brand-accent hover:underline flex items-center gap-1"
                    >
                      <Eye className="w-3 h-3" />
                      {doc.file_name}
                    </a>
                  )}
                  {doc.expiry_date && (
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Expires: {new Date(doc.expiry_date).toLocaleDateString()}
                      {new Date(doc.expiry_date) < new Date() && (
                        <span className="text-red-400 ml-2 font-medium">EXPIRED</span>
                      )}
                    </p>
                  )}
                  {doc.status === 'rejected' && doc.rejection_reason && (
                    <div className="flex items-start gap-1.5 mt-2 bg-red-500/10 rounded-lg p-2">
                      <AlertTriangle className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" />
                      <p className="text-[10px] text-red-400">{doc.rejection_reason}</p>
                    </div>
                  )}
                </div>
                {doc.status === 'pending' && (
                  <div className="flex gap-2 ml-4">
                    <Button size="sm" onClick={() => onApprove(doc.document_id || doc.id)} disabled={actionLoading}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8 px-3">
                      <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => onReject(doc.document_id || doc.id)} disabled={actionLoading}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs h-8 px-3">
                      <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function EquipmentTab({ equipment, equipmentDocs, onApprove, onReject, actionLoading }) {
  if (equipment.length === 0) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-8 text-center">
          <Truck className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">No equipment registered yet</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {equipment.map(eq => {
        const docs = equipmentDocs[eq.equipment_id] || [];
        const isPending = eq.status === 'pending_approval' || eq.status === 'pending';
        const statusClasses = isPending
          ? 'bg-amber-500/20 text-amber-400'
          : eq.status === 'active'
            ? 'bg-emerald-500/20 text-emerald-400'
            : 'bg-red-500/20 text-red-400';

        return (
          <Card key={eq.equipment_id} className="bg-card border-border">
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <p className="text-sm text-foreground font-medium">
                      {eq.year} {eq.make} {eq.model}
                    </p>
                    <Badge className={`text-[10px] ${statusClasses}`}>
                      {(eq.status || 'pending').replace(/_/g, ' ').toUpperCase()}
                    </Badge>
                  </div>
                  {eq.unit_number && <p className="text-[10px] text-muted-foreground">Unit #{eq.unit_number}</p>}
                </div>
                {isPending && (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => onApprove(eq.equipment_id)} disabled={actionLoading}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8 px-3">
                      <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve Equipment
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => onReject(eq.equipment_id)} disabled={actionLoading}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs h-8 px-3">
                      <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                    </Button>
                  </div>
                )}
              </div>

              {/* Equipment details grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                <DetailItem label="VIN" value={eq.vin || '—'} />
                <DetailItem label="License Plate" value={eq.license_plate || '—'} />
                <DetailItem label="Province" value={eq.province || '—'} />
                <DetailItem label="Make" value={eq.make || '—'} />
                <DetailItem label="Model" value={eq.model || '—'} />
                <DetailItem label="Year" value={eq.year || '—'} />
              </div>

              {/* Equipment documents/photos gallery */}
              {docs.length > 0 && (
                <div>
                  <p className="text-[10px] text-muted-foreground tracking-wider font-bold mb-2">DOCUMENTS / PHOTOS</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {docs.map((d, i) => (
                      <a key={d.id || i} href={d.file_url || '#'} target="_blank" rel="noopener noreferrer"
                        className="bg-muted/50 rounded-lg p-2 hover:bg-muted transition-colors text-center">
                        {d.file_url && (d.file_url.match(/\.(jpg|jpeg|png|gif|webp)$/i)) ? (
                          <img src={d.file_url} alt={d.document_type || 'Equipment photo'} className="w-full h-20 object-cover rounded mb-1" />
                        ) : (
                          <FileText className="w-8 h-8 text-muted-foreground mx-auto mb-1" />
                        )}
                        <p className="text-[10px] text-muted-foreground truncate capitalize">
                          {(d.document_type || d.file_name || 'Document').replace(/_/g, ' ')}
                        </p>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function ReadinessTab({ driver, readiness, onApproveDriver, onRejectDriver, actionLoading }) {
  if (!readiness) return null;

  const checks = [
    { label: 'Profile Completion (>= 80%)', passed: readiness.profileComplete, detail: `${driver.profile_completion_pct || 0}%` },
    { label: 'Terms Accepted', passed: readiness.termsAccepted },
    { label: "Driver's License Approved", passed: readiness.licenseApproved },
    { label: 'Equipment Approved', passed: readiness.equipmentApproved },
    { label: 'No Expired Documents', passed: readiness.noExpiredDocs },
  ];

  const allPassed = checks.every(c => c.passed);

  return (
    <div className="max-w-2xl space-y-6">
      <Card className="bg-card border-border">
        <CardContent className="p-5 space-y-4">
          <p className="text-[10px] text-muted-foreground tracking-wider font-bold">ONBOARDING CHECKLIST</p>
          <div className="space-y-3">
            {checks.map((c, i) => (
              <div key={i} className={`flex items-center gap-3 p-3 rounded-lg ${c.passed ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
                {c.passed
                  ? <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  : <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                }
                <span className="text-sm text-foreground flex-1">{c.label}</span>
                {c.detail && <span className="text-xs text-muted-foreground">{c.detail}</span>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Action buttons */}
      {!driver.onboarding_completed && (
        <div className="flex gap-4">
          <Button
            onClick={onApproveDriver}
            disabled={actionLoading}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm h-12 rounded-2xl"
          >
            <CheckCircle className="w-5 h-5 mr-2" />
            {actionLoading ? 'Approving...' : 'Approve Driver'}
          </Button>
          <Button
            onClick={onRejectDriver}
            disabled={actionLoading}
            variant="outline"
            className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10 font-bold text-sm h-12 rounded-2xl"
          >
            <XCircle className="w-5 h-5 mr-2" />
            Reject Driver
          </Button>
        </div>
      )}

      {driver.onboarding_completed && (
        <Card className="bg-emerald-500/10 border-emerald-500/20">
          <CardContent className="p-5 text-center">
            <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
            <p className="text-lg font-bold text-emerald-400">Driver Onboarded</p>
            <p className="text-xs text-muted-foreground mt-1">This driver has been approved and is fully onboarded.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[10px] text-muted-foreground">{label}</span>
      <span className="text-xs text-foreground">{value}</span>
    </div>
  );
}

function DetailItem({ label, value }) {
  return (
    <div className="bg-muted/50 rounded-lg p-2">
      <p className="text-[10px] text-muted-foreground">{label}</p>
      <p className="text-xs text-foreground font-medium">{value}</p>
    </div>
  );
}
